import axios, { AxiosResponse, AxiosProgressEvent } from 'axios'
import ApiService from './api.service'
import type { 
  DocumentUploadResponse, 
  DocumentViewResponse, 
  DocumentDownloadResponse,
  DocumentDeleteResponse,
  Document
} from '@/types/services'

interface DocumentMetadata {
  size: number | null
  type: string
}

class DocumentService extends ApiService {
  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Project Document Error'
      }
    })
  }
  
  /**
   * Get documents for a specific project
   * @param projectId - The ID of the project
   * @returns Promise that resolves with the project documents
   */
  async getProjectDocuments(projectId: string): Promise<Document[]> {
    if (!projectId) {
      return Promise.reject({ message: 'No project ID provided' })
    }
    
    const response = await this.get(`/api/project/${projectId}/documents`)
    return response as Document[]
  }

  /**
   * Upload documents to a project
   * @param projectId - The ID of the project
   * @param formData - Form data containing the documents files
   * @returns Promise that resolves when documents are uploaded
   */
  async uploadProjectDocuments(
    projectId: string, 
    formData: FormData,
    // Optional progress callback forwarded to axios onUploadProgress
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): Promise<DocumentUploadResponse> {
    if (!projectId) {
      return Promise.reject({ message: 'No project ID provided' })
    }
    
    const response = await this.post(`/api/project/${projectId}/documents/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      // Forward the onUploadProgress handler to axios via ApiService.post
      onUploadProgress
    })
    return response as DocumentUploadResponse
  }

  /**
   * Delete a project document
   * @param projectId - The ID of the project
   * @param documentId - The ID of the document to delete
   * @returns Promise that resolves when the document is deleted
   */
  async deleteProjectDocument(
    projectId: string, 
    documentId: string
  ): Promise<DocumentDeleteResponse> {
    if (!projectId || !documentId) {
      return Promise.reject({ message: 'Project ID and document ID are required' })
    }
    
    const response = await this.delete(`/api/project/${projectId}/documents/${documentId}`)
    return response as DocumentDeleteResponse
  }

  /**
   * Get document for viewing in browser
   * @param projectId - The ID of the project
   * @param documentId - The ID of the document to view
   * @returns Promise that resolves with the document blob data
   */
  async getDocumentForView(
    projectId: string, 
    documentId: string
  ): Promise<DocumentViewResponse> {
    if (!projectId || !documentId) {
      return Promise.reject({ message: 'Project ID and document ID are required' })
    }
    
    try {
      // Use axios directly for better control over binary data
      const response: AxiosResponse<Blob> = await axios({
        url: `/api/project/${projectId}/documents/${documentId}`,
        method: 'GET',
        responseType: 'blob',
        headers: this.getHeaders()
      })
      
      // Get content length from headers if available
      const contentLength = response.headers['content-length']
      
      // Return both the blob and metadata
      return {
        data: response.data, // This is the actual Blob object
        metadata: {
          size: contentLength ? parseInt(contentLength, 10) : null,
          type: response.headers['content-type'] || 'application/octet-stream'
        }
      }
    } catch (error) {
      console.error('Error loading document for view:', error)
      return Promise.reject({ 
        message: error instanceof Error ? error.message : 'Error loading document', 
        originalError: error 
      })
    }
  }

  /**
   * Download a project document
   * @param projectId - The ID of the project
   * @param documentId - The ID of the document to download
   * @param filename - Optional filename for the download
   * @returns Promise that resolves with the document data
   */
  async downloadDocument(
    projectId: string, 
    documentId: string, 
    filename?: string
  ): Promise<DocumentDownloadResponse> {
    if (!projectId || !documentId) {
      return Promise.reject({ message: 'Project ID and document ID are required' })
    }
    
    try {
      // For binary data, we need to use axios directly to get the correct response handling
      const response: AxiosResponse<Blob> = await axios({
        url: `/api/project/${projectId}/documents/${documentId}`,
        method: 'GET',
        responseType: 'blob', // Important: responseType must be blob
        headers: this.getHeaders()
      })
      
      // Get the content type directly from the response
      const contentType = response.headers['content-type'] || 'application/octet-stream'
      
      // Use the response.data directly - it's already a blob when responseType is 'blob'
      const blob = response.data  // response.data is already a blob when responseType is 'blob'
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Prepare metadata for return value
      const contentLength = response.headers['content-length']
      const metadata: DocumentMetadata = {
        size: contentLength ? parseInt(contentLength, 10) : null,
        type: contentType
      }
      
      // Get filename from response headers if available
      const contentDisposition = response.headers['content-disposition']
      
      // Only try to extract filename if we don't already have one and contentDisposition exists
      if (!filename && contentDisposition) {
        // Try different filename patterns (quoted and unquoted)
        const filenameMatch = 
          contentDisposition.match(/filename="([^"]+)"/) || 
          contentDisposition.match(/filename=([^;]+)/)
        
        if (filenameMatch) {
          filename = filenameMatch[1].trim()
        }
      }
      
      // Ensure we have a filename, even if we have to generate one
      if (!filename) {
        const timestamp = new Date().getTime()
        const extension = contentType.includes('pdf') ? '.pdf' : 
                         contentType.includes('image') ? '.png' : 
                         '.bin'
        filename = `document_${documentId}_${timestamp}${extension}`
      }
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
      // Return success response with metadata
      return { 
        success: true, 
        metadata 
      }
      
    } catch (error) {
      console.error('Error downloading document:', error)
      return Promise.reject({ 
        message: error instanceof Error ? error.message : 'Error downloading document', 
        originalError: error 
      })
    }
  }
}

// Create and export a singleton instance
export default new DocumentService()
